import React, { FC, useEffect, useMemo, useState } from 'react';
import { EFacetType_Generic, Facet, FilterFacetOption, useFilter } from '$shared/utils/filter';
import { facetOptionMapper } from '../utils/facetOptionMapper';
import FacetCheckbox from '$shared/components/facets/facet-checkbox';
import { FacetWrapper } from '$shared/components/facets/shared/facet-wrapper';
import FacetSelector from '$shared/components/facets/facet-selector';
import { IFacetOption } from '$shared/components/facets';
import { FacetSkeleton } from './facet-skeleton';
import { FILTER_PAGE_KEY, FILTER_SEARCH_TEXT_KEY } from '$shared/utils/filter/service/constants';

type MappedOption = IFacetOption<FilterFacetOption>;
type FacetWithMappedOptions = {
    facet: Facet;
    mappedOptions: MappedOption[];
};

const VALUE_SEP = '↕';

type FacetMetaCache = {
    facets?: Record<
        string,
        { title: string; suffix?: string; type: EFacetType_Generic; url?: string }
    >;
    labels?: Record<string, string>;
};
const CACHE_KEY = 'facets-meta';

const toLabel = (raw: string) => {
    if (!raw) return '';
    if (raw.includes(VALUE_SEP)) {
        const [, right] = raw.split(VALUE_SEP);
        if (right && right.trim()) return right.trim();
    }
    return raw;
};

const readCache = (): FacetMetaCache => {
    try {
        return JSON.parse(sessionStorage.getItem(CACHE_KEY) || '{}') as FacetMetaCache;
    } catch {
        return {};
    }
};

const writeCache = (next: FacetMetaCache) => {
    sessionStorage.setItem(CACHE_KEY, JSON.stringify(next));
};

export const DynamicFacets: FC = () => {
    const { facets, setFacet, isLoadingInitially, activeQueryState } = useFilter();
    const [facetsWithMappedOptions, setFacetsWithMappedOptions] = useState<FacetWithMappedOptions[]>();
    const [cachedMeta, setCachedMeta] = useState<FacetMetaCache>(() => readCache());
    const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });

    const labelsFromFacets = useMemo(() => {
        const m: Record<string, string> = {};
        (facets ?? []).forEach(f => (f.options ?? []).forEach(opt => {
            const mapped = facetOptionMapper(opt);
            if (mapped?.key && mapped?.name) m[mapped.key] = mapped.name;
        }));
        return m;
    }, [facets]);

    const ensureSelectedVisible = (beOptions: MappedOption[], selectedKeys: string[]): MappedOption[] => {
        if (!selectedKeys?.length) return beOptions;
        const existing = new Set(beOptions.map(o => o.key));
        const extras: MappedOption[] = selectedKeys
            .filter((k): k is string => !!k && !existing.has(k))
            .map(k => ({
                key: k,
                name: labelsFromFacets[k] ?? cachedMeta.labels?.[k] ?? toLabel(k),
                selected: true,
                disabled: false,
                count: 0,
            } as MappedOption));
        return [...beOptions, ...extras].sort((a, b) => collator.compare(a.name ?? '', b.name ?? ''));
    };

    const selectedOnlyFacets: FacetWithMappedOptions[] = useMemo(() => {
        const state = activeQueryState ?? {};
        const urlFacets = Object.entries(state).filter(
            ([k]) => k !== FILTER_PAGE_KEY && k !== FILTER_SEARCH_TEXT_KEY
        );
        if (!urlFacets.length) return [];

        const beFacetById =
            (facets || []).reduce<Record<string, Facet>>((acc, f) => {
                if (f?.id != null) acc[String(f.id)] = f;
                return acc;
            }, {}) || {};

        return urlFacets.map(([facetId, values]) => {
            const proto = beFacetById[facetId];
            const cached = cachedMeta.facets?.[facetId];

            const opts: MappedOption[] =
                (values ?? []).filter(v => typeof v === 'string' && v.includes(VALUE_SEP))
                    .filter((v): v is string => {
                        if (!v) return false;
                        if (!v.includes(VALUE_SEP)) return false;
                        const prefix = v.split(VALUE_SEP)[0];
                        if (prefix.startsWith('ConnectionParent') || prefix.startsWith('ConnectionChild')) {
                            return false;
                        }
                        return true;
                    })
                    .map((v) => {
                        const label = labelsFromFacets[v] ?? cachedMeta.labels?.[v] ?? toLabel(v);
                        return {
                            key: v,
                            name: label,
                            selected: true,
                            disabled: false,
                            count: 0,
                        } as MappedOption;
                    }) || [];

            const fauxFacet: Facet = {
                id: proto?.id ?? facetId,
                title: proto?.title ?? cached?.title ?? facetId,
                suffix: proto?.suffix,
                url: proto?.url,
                type: proto?.type ?? cached?.type ?? EFacetType_Generic.CHECKBOX,
            };

            return { facet: fauxFacet, mappedOptions: opts };
        });
    }, [activeQueryState, facets]);

    useEffect(() => {
        if (!facets || !facets.length) return;

        const next: FacetMetaCache = {
            facets: { ...(cachedMeta.facets || {}) },
            labels: { ...(cachedMeta.labels || {}) },
        };

        facets.forEach(f => {
            const id = f?.id != null ? String(f.id) : undefined;
            if (!id) return;

            next.facets![id] = {
                title: f.title || id,
                suffix: f.suffix,
                type: f.type ?? EFacetType_Generic.CHECKBOX,
                url: f.url,
            };

            (f.options || []).forEach(opt => {
                const mapped = facetOptionMapper(opt);
                if (mapped?.key) {
                    next.labels![mapped.key] = mapped.name ?? toLabel(mapped.key);
                }
            });
        });

        writeCache(next);
        setCachedMeta(next);
    }, [facets]);

    useEffect(() => {
        if (!facets) {
            setFacetsWithMappedOptions(selectedOnlyFacets.length ? selectedOnlyFacets : []);
            return;
        }

        const byFacetId: Record<string, string[]> = Object.entries(activeQueryState ?? {}).reduce((acc, [k, vals]) => {
            if (k === FILTER_PAGE_KEY || k === FILTER_SEARCH_TEXT_KEY) return acc;
            acc[k] = (vals ?? []).filter((v): v is string => !!v && v.includes(VALUE_SEP));
            return acc;
        }, {} as Record<string, string[]>);

        const mapped = facets.map(facet => {
            const beMapped = (facet.options || []).map(facetOptionMapper);
            const facetId = facet?.id != null ? String(facet.id) : '';
            const selectedForThisFacet = facetId ? byFacetId[facetId] ?? [] : [];
            const merged = ensureSelectedVisible(beMapped, selectedForThisFacet);

            const finalOpts = merged.map(o => ({
                ...o,
                selected: selectedForThisFacet.includes(o.key ?? ''),
            }));

            return { facet, mappedOptions: finalOpts };
        });

        setFacetsWithMappedOptions(mapped.length ? mapped : selectedOnlyFacets);
    }, [facets, activeQueryState, selectedOnlyFacets]);

    const handleFacetChange = <T,>({
                                       facet,
                                       change,
                                       overwrite,
                                   }: {
        change: IFacetOption<T> | IFacetOption<T>[] | undefined;
        facet: Facet;
        overwrite?: boolean;
    }) => {
        const options = Array.isArray(change)
            ? change.map((c) => `${c.key}`)
            : change
                ? change.key
                : [];
        setFacet?.({ facet: `${facet.id}`, options, overwrite });
    };

    const facetSwitch = (facet: Facet, mappedOptions: MappedOption[]) => {
        switch (facet.type) {
            case EFacetType_Generic.CHECKBOX:
                return (
                    <FacetCheckbox
                        options={mappedOptions}
                        initialLoadSize={7}
                        handleChange={change => handleFacetChange({ change, facet })}
                    />
                );
            case EFacetType_Generic.DROPDOWN:
                return (
                    <FacetSelector
                        menuPlacement="auto"
                        options={mappedOptions}
                        handleChange={(change, overwrite) => handleFacetChange({ change, facet, overwrite })}
                    />
                );
            default:
                return null;
        }
    };

    return (
        <>
            {facetsWithMappedOptions
                ?.filter(({ mappedOptions }) => mappedOptions.length > 0)
                .map(({ facet, mappedOptions }) => (
                    <FacetWrapper
                        key={facet.id}
                        headline={facet.suffix ? `${facet.title} (${facet.suffix})` : `${facet.title}`}
                        documentUrl={facet.url}
                    >
                        {isLoadingInitially ? <FacetSkeleton facetType={facet.type} /> : facetSwitch(facet, mappedOptions)}
                    </FacetWrapper>
                ))}
        </>
    );
};

export default DynamicFacets;